import type { ITeam } from "@bbj-types/team";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import { axios } from "./axios";
import type { ExportSerializer, ExportType } from "./types";

export interface IPostExportCandidatesBody {
  candidateIds: ObjectId[];
  fileType: ExportSerializer;
}

export const usePostExportCandidates = () => {
  const [state, execute, cancel] = axios<string, IPostExportCandidatesBody, unknown>(
    {
      url: "/candidates",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateIds: IPostExportCandidatesBody["candidateIds"], fileType: IPostExportCandidatesBody["fileType"]) => {
      return execute({
        data: {
          candidateIds,
          fileType: fileType,
        },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export interface IGetExportCandidatesQuery {
  skip?: number;
  limit?: number;
  authors?: ObjectId[];
  startDate?: Date;
  endDate?: Date;
}

export interface IGetExportCandidates {
  exports: {
    _id: ObjectId;
    organisation: ObjectId;
    author: {
      _id: ObjectId;
      firstName: string;
      lastName: string;
      email: string;
    };
    type: ExportType.CANDIDATE;
    document: {
      _id: ObjectId;
      url: string;
      name: string;
      mimeType: string;
    };
    meta: {
      candidates: ObjectId[];
      team: Pick<ITeam, "_id" | "name">[];
    };
    createdAt: Date;
  }[];
  skip: number;
  limit: number;
  total: number;
}

export const useGetExportCandidates = () => {
  const [state, execute, cancel] = axios<IGetExportCandidates, unknown, unknown>(
    {
      url: "/candidates",
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );
  const callback = useCallback(
    (query: IGetExportCandidatesQuery) => {
      return execute({
        params: query,
      });
    },
    [execute]
  );
  return [state, callback, cancel] as const;
};
